import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import DuplicateTournamentDialog from "../components/DuplicateTournamentDialog";
import PendingApprovalNotice from "../components/PendingApprovalNotice";
import TournamentBottomNav from "../components/TournamentBottomNav";
import TournamentLifecycleCard from "../components/TournamentLifecycleCard";
import { formatDate } from "../lib/dateFormat";
import { getEliminationDirectAdvancerSummary } from "../lib/elimination";
import {
  api,
  ApiError,
  type EliminationFormatConfig,
  type TournamentDetailResponse,
  type TournamentMatch,
} from "../lib/api";

function isNotPlayed(match: TournamentMatch): boolean {
  return String(match.status) === "cancelled";
}

function isPlayedStatus(match: TournamentMatch): boolean {
  return ["completed", "walkover", "no_show", "defaulted", "retired", "double_walkover"].includes(String(match.status));
}

export default function AdminTournamentHomePage() {
  const { id } = useParams<{ id: string }>();
  const tournamentId = id ? Number(id) : NaN;
  const { token } = useAuth();

  const [detail, setDetail] = useState<TournamentDetailResponse["data"] | null>(null);
  const [matches, setMatches] = useState<TournamentMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  // Cambiar de torneo con una carga pendiente: la respuesta vieja no pisa
  // el inicio del torneo nuevo (ni su aviso de inscripciones sin aprobar).
  const loadSeqRef = useRef(0);

  const load = useCallback(async (options: { silent?: boolean } = {}) => {
    if (!token || Number.isNaN(tournamentId)) return;
    const seq = ++loadSeqRef.current;
    // Tras cerrar/reabrir se refresca en silencio (sin desmontar el Resumen).
    if (!options.silent) setLoading(true);
    setError(null);
    try {
      const [tournamentResponse, matchesResponse] = await Promise.all([
        api.tournaments.get(token, tournamentId),
        api.matches.list(token, tournamentId),
      ]);
      if (seq !== loadSeqRef.current) return;
      setDetail(tournamentResponse.data);
      setMatches(matchesResponse.data.matches);
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar el torneo.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [token, tournamentId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (Number.isNaN(tournamentId)) {
    return (
      <section className="card">
        <p className="form-error">ID de torneo inválido.</p>
      </section>
    );
  }

  if (loading) {
    return (
      <section className="card">
        <p className="muted">Cargando torneo…</p>
      </section>
    );
  }

  if (error || !detail) {
    return (
      <section className="card">
        <p className="form-error">{error ?? "Torneo no encontrado."}</p>
        <Link className="btn btn--ghost" to="/admin/tournaments">
          Volver
        </Link>
      </section>
    );
  }

  const { tournament, settings, setup_status: setupStatus, format_config: formatConfig } = detail;
  const counts = setupStatus.counts || {};
  const isElimination = tournament.tournament_type === "elimination";
  const eliminationConfig = isElimination && formatConfig?.format_type === "elimination"
    ? (formatConfig.config_json as EliminationFormatConfig)
    : null;
  // Los «No jugado» (cancelados al cerrar) no cuentan como pendientes.
  const matchTotal = matches.filter((match) => !isNotPlayed(match)).length || counts.matches || 0;
  const matchResults = matches.filter(isPlayedStatus).length;
  const byeCount = matches.filter((match) => !match.player1 || !match.player2).length;
  const directAdvancerSummary = getEliminationDirectAdvancerSummary({
    participantCount: counts.participants ?? 0,
    technicalByeCount: byeCount,
    config: eliminationConfig,
  });
  const preliminaryMatches = matches.filter((match) => match.round_label === "Preliminar");
  const preliminaryParticipantIds = new Set<number>();
  for (const match of preliminaryMatches) {
    if (match.player1_participant_id !== null) preliminaryParticipantIds.add(match.player1_participant_id);
    if (match.player2_participant_id !== null) preliminaryParticipantIds.add(match.player2_participant_id);
  }
  const usesActualDirectAdvancers = matches.length > 0 && preliminaryMatches.length > 0;
  const actualDirectAdvancerCount = usesActualDirectAdvancers
    ? Math.max((counts.participants ?? 0) - preliminaryParticipantIds.size, 0)
    : 0;
  const directAdvancerLabel = usesActualDirectAdvancers
    ? `${actualDirectAdvancerCount} descanso${actualDirectAdvancerCount === 1 ? "" : "s"} reales`
    : directAdvancerSummary.primaryLabel;
  const pendingMatches = Math.max(matchTotal - matchResults, 0);
  const pendingApproval = detail.pending_approval ?? { total: 0, paid: 0 };

  return (
    <>
      <section className="card tournament-home-hero">
        <div className="card__header-row">
          <div>
            <h1 className="card__title">{tournament.name}</h1>
            {tournament.status === "closed" ? (
              <span className="tournament-status tournament-status--closed">Cerrado · solo lectura</span>
            ) : null}
          </div>
        </div>
      </section>

      <section className="card">
        <h2 className="card__title">Resumen</h2>
        <div className="stats-grid">
          <div className="stat-card">
            <span className="stat-card__label">Jugadores</span>
            <strong>{counts.participants ?? 0}</strong>
          </div>
          <div className="stat-card">
            <span className="stat-card__label">Grupos</span>
            <strong>{counts.groups ?? 0}</strong>
          </div>
          <div className="stat-card">
            <span className="stat-card__label">
              {isElimination ? "Partidos del cuadro" : "Enfrentamientos publicados"}
            </span>
            <strong>{isElimination ? matchTotal : (counts.rounds_published ?? 0)}</strong>
            {isElimination && (
              <small className="stat-card__hint">{directAdvancerLabel}</small>
            )}
          </div>
          <div className="stat-card">
            <span className="stat-card__label">
              {isElimination ? "Resultados capturados" : "Partidos en calendario"}
            </span>
            <strong>{isElimination ? `${matchResults}/${matchTotal}` : matchTotal}</strong>
            {isElimination && <small className="stat-card__hint">{pendingMatches} pendiente(s)</small>}
          </div>
        </div>
      </section>

      <section className="card tournament-admin-actions-card">
        <div>
          <p className="eyebrow">Inscripciones</p>
          <h2 className="card__title">Pagos y Estado de cuenta del torneo</h2>
          <p className="muted">Controla quién ya pagó</p>
        </div>
        <Link className="button button--primary" to={`/admin/tournaments/${tournament.id}/registrations`}>
          💳 Abrir inscripciones y pagos
        </Link>
        {tournament.status !== "closed" && pendingApproval.total > 0 ? (
          <div className="tournament-admin-actions-card__notice">
            <PendingApprovalNotice
              total={pendingApproval.total}
              paid={pendingApproval.paid}
              autoSendWelcome={settings.auto_send_welcome !== false}
              to={`/admin/tournaments/${tournament.id}/registrations?pendientes=1`}
            />
          </div>
        ) : null}
      </section>

      {token ? (
        <TournamentLifecycleCard
          token={token}
          tournamentId={tournament.id}
          tournamentName={tournament.name}
          onChange={() => void load({ silent: true })}
        />
      ) : null}

      {token && detail.can_duplicate ? (
        <section className="card tournament-duplicate-card">
          <div>
            <h2 className="card__title">¿Lo repetirás?</h2>
            <p className="muted">
              Crea el siguiente torneo con las mismas categorías, formulario de inscripción y reglamento. Por lo
              general solo cambias nombre, fechas, póster y grupo de WhatsApp; si hay algo más que revisar (por ejemplo,
              la forma de cobro), te avisamos en el paso 1.
            </p>
          </div>
          <button type="button" className="btn btn--ghost" onClick={() => setDuplicateOpen(true)}>
            Duplicar torneo
          </button>
          {duplicateOpen ? (
            <DuplicateTournamentDialog
              token={token}
              tournament={tournament}
              preview={detail.duplicate_preview}
              onClose={() => setDuplicateOpen(false)}
            />
          ) : null}
        </section>
      ) : null}

      <details className="card tournament-details-card" open>
        <summary className="tournament-details-card__summary">
          <span className="card__title">Datos del Torneo</span>
          <span className="tournament-details-card__chevron" aria-hidden="true">▾</span>
        </summary>
        <ul className="list list--compact tournament-details-card__content">
          <li>
            <strong>Sede:</strong> {tournament.venue_name || "—"}
          </li>
          <li>
            <strong>Fechas:</strong> {formatDate(tournament.start_date)} → {formatDate(tournament.end_date)}
          </li>
          {tournament.registration_deadline && (
            <li>
              <strong>Cierre de inscripciones:</strong>{" "}
              {formatDate(tournament.registration_deadline)}
            </li>
          )}
          <li>
            <strong>Costo:</strong> {tournament.registration_cost || "—"}
          </li>
          <li>
            <strong>Pelota oficial:</strong> {tournament.official_ball || "—"}
          </li>
          <li>
            <strong>Uso de calendario:</strong>{" "}
            {settings.uses_court_calendar
              ? "Sí, admin asigna cancha/fecha/hora"
              : "No, jugadores coordinan por WhatsApp/llamada"}
          </li>
        </ul>
      </details>

      <TournamentBottomNav tournamentId={tournament.id} />
    </>
  );
}
